import React, { useEffect, useMemo, useRef, useState } from "react";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {getUserData, updateUserAvatar} from "../../../store/cabinet/action";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_AVATAR_SIZE = 128;

const getErrorSource = (formErr) => {
    return (
        formErr?.response?.data?.errors ||
        formErr?.data?.errors ||
        formErr?.errors ||
        {}
    );
};

const getFieldError = (formErr, fieldName) => {
    const errors = getErrorSource(formErr);
    const fieldError = errors?.[fieldName];

    if (!fieldError) return "";

    if (Array.isArray(fieldError)) {
        return fieldError[0] || "";
    }

    return String(fieldError);
};

const getGeneralError = (formErr) => {
    return (
        formErr?.response?.data?.message ||
        formErr?.data?.message ||
        formErr?.message ||
        ""
    );
};

const validateAvatarSize = (file) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const imageUrl = URL.createObjectURL(file);

        image.onload = () => {
            const result = {
                isValid:
                    image.width === image.height &&
                    image.width >= MIN_AVATAR_SIZE &&
                    image.height >= MIN_AVATAR_SIZE,
                width: image.width,
                height: image.height,
            };

            URL.revokeObjectURL(imageUrl);
            resolve(result);
        };

        image.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            reject();
        };

        image.src = imageUrl;
    });
};

const CabinetProfileAvatar = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const fileInputRef = useRef(null);

    const userData = useSelector(store => store.cabinet.userData);
    const updateUserAvatarStarted = useSelector(store => store.cabinet.updateUserAvatarStarted);
    const updateUserAvatarErr = useSelector(store => store.cabinet.updateUserAvatarErr);

    const [avatarPreview, setAvatarPreview] = useState("");
    const [avatarFile, setAvatarFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [isServerErrorHidden, setIsServerErrorHidden] = useState(true);

    const userName = useMemo(() => {
        const firstName = userData?.firstName || userData?.first_name || "";
        const lastName = userData?.lastName || userData?.last_name || "";
        const fullName = `${firstName} ${lastName}`.trim();

        return fullName || userData?.name || "Steven Johnson";
    }, [userData]);

    const avatarSrc =
        avatarPreview ||
        userData?.avatar ||
        userData?.avatarUrl ||
        userData?.avatar_url ||
        userData?.photo ||
        userData?.photoUrl ||
        "";

    const avatarLetter = userName?.trim()?.charAt(0)?.toUpperCase() || "S";

    const accountType = userData?.account_type || userData?.accountType || t("Личный аккаунт");

    const isActive =
        userData?.subscription_status === "active" ||
        userData?.status === "active";

    const avatarServerError =
        getFieldError(updateUserAvatarErr, "avatar") ||
        getGeneralError(updateUserAvatarErr);

    const visibleAvatarError =
        fileError ||
        (!isServerErrorHidden && !updateUserAvatarStarted ? avatarServerError : "");

    const uploadClickHandler = () => {
        if (updateUserAvatarStarted) return;

        setFileError("");
        setIsServerErrorHidden(true);

        fileInputRef.current?.click();
    };

    const uploadHandler = async (event) => {
        const file = event.target.files?.[0];

        if (!file) return;

        setFileError("");
        setIsServerErrorHidden(true);

        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

        if (!allowedTypes.includes(file.type)) {
            setFileError("Only JPG, PNG, or WEBP files can be uploaded.");
            event.target.value = "";
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setFileError("The file size must not exceed 5MB.");
            event.target.value = "";
            return;
        }

        try {
            const imageValidation = await validateAvatarSize(file);

            if (!imageValidation.isValid) {
                setFileError("The avatar must be a square image of at least 128 pixels a side.");
                event.target.value = "";
                return;
            }
        } catch (error) {
            setFileError("Could not validate the image. Please try another file.");
            event.target.value = "";
            return;
        }

        const previewUrl = URL.createObjectURL(file);

        setAvatarPreview(previewUrl);
        setAvatarFile(file);

        event.target.value = "";
    };

    const cancelAvatarHandler = () => {
        if (updateUserAvatarStarted) return;

        setAvatarPreview("");
        setAvatarFile(null);
        setFileError("");
        setIsServerErrorHidden(true);
    };

    const successHandler = () => {
        toast(t("Аватар успешно обновлен"));

        setAvatarFile(null);
        setFileError("");
        setIsServerErrorHidden(true);

        dispatch(getUserData());
    };

    const saveAvatarHandler = () => {
        if (updateUserAvatarStarted) return;
        if (!avatarFile) return;

        setFileError("");
        setIsServerErrorHidden(false);

        dispatch(updateUserAvatar({
            avatar: avatarFile,
        }, successHandler));
    };

    useEffect(() => {
        return () => {
            if (avatarPreview) {
                URL.revokeObjectURL(avatarPreview);
            }
        };
    }, [avatarPreview]);

    return (
        <div className={Styles.container}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetCard}`}>
                <div className={Styles.title}>
                    {t("Управляйте основной информацией")}
                </div>

                <div className={Styles.profileWrap}>
                    <div className={Styles.avatarWrap}>
                        {avatarSrc ? (
                            <img
                                src={avatarSrc}
                                alt={userName}
                                className={Styles.avatarImg}
                            />
                        ) : (
                            <div className={Styles.avatarFallback}>
                                {avatarLetter}
                            </div>
                        )}
                    </div>

                    <div className={Styles.infoWrap}>
                        <div className={Styles.accountType}>
                            {t(accountType)}
                        </div>

                        <div className={Styles.userName}>
                            {userName}
                        </div>

                        <div className={`${Styles.statusBadge} ${isActive ? Styles.statusBadgeActive : Styles.statusBadgeInactive}`}>
                            <span className={Styles.statusIcon} />

                            <span>
                                {isActive ? t("Активен") : t("Неактивен")}
                            </span>
                        </div>

                        <div className={Styles.uploadArea}>
                            {visibleAvatarError && (
                                <div className={Styles.uploadErrorText}>
                                    {visibleAvatarError}
                                </div>
                            )}

                            {!avatarFile ? (
                                <button
                                    type="button"
                                    className={Styles.uploadBox}
                                    onClick={uploadClickHandler}
                                    disabled={updateUserAvatarStarted}
                                >
                                    <span className={Styles.uploadTitle}>
                                        <span className={Styles.uploadArrow}>↑</span>
                                        {t("Загрузить аватар")}
                                    </span>

                                    <span className={Styles.uploadNote}>
                                        {t("JPG, PNG или WEBP. Максимум 5MB.")}
                                    </span>
                                </button>
                            ) : (
                                <div className={`${Styles.uploadBox} ${Styles.uploadBoxWithActions}`}>
                                    <div className={Styles.uploadSelectedText}>
                                        {t("Аватар выбран")}
                                    </div>

                                    <div className={Styles.avatarActions}>
                                        <button
                                            type="button"
                                            className={Styles.saveAvatarBtn}
                                            onClick={saveAvatarHandler}
                                            disabled={updateUserAvatarStarted}
                                        >
                                            {updateUserAvatarStarted ? t("Сохранение") : t("Сохранить")}
                                        </button>

                                        <button
                                            type="button"
                                            className={Styles.cancelAvatarBtn}
                                            onClick={cancelAvatarHandler}
                                            disabled={updateUserAvatarStarted}
                                        >
                                            {t("Отмена")}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className={Styles.fileInput}
                            onChange={uploadHandler}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CabinetProfileAvatar;